import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../apiService';
import DialogShell from '../ui/DialogShell';
import { useAuth, useAuthActions } from '../../context/AuthContext';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import { formatDateTime } from '../../utils/formatDateTime';

interface UserRow {
    id: string;
    email: string;
    name: string;
    company: string | null;
    role: string;
    is_active: boolean;
    has_password: boolean;
    last_login: string | null;
    // BAR-946: may this person use the BARK agent over MCP. A fixed policy in
    // auth_service config (not editable here) — shown so admins can see who.
    mcp_agent_access?: boolean;
}

interface UsersTableProps {
    authToken: string;
    currentUserUid?: string;
    refreshKey: number;
}

type RowState = 'idle' | 'busy';
type PendingRoleChange = { user: UserRow; newRole: string };

const FIELD_CLASS =
    'rounded-md border border-border-default bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus-visible:shadow-bark-focus';

interface EditUserDialogProps {
    authToken: string;
    user: UserRow;
    onCancel: () => void;
    onSaved: (changes: { name?: string; company?: string }) => void;
}

// Name and company of another person. auth_service accepts only a company that
// has an organization (an unknown claim is a 400), so the company is picked
// from the organizations list, not typed.
function EditUserDialog({ authToken, user, onCancel, onSaved }: EditUserDialogProps) {
    const [name, setName] = useState(user.name || '');
    const [company, setCompany] = useState(user.company || '');
    const [companies, setCompanies] = useState<string[] | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        api.listOrganizations(authToken, { includeFree: true })
            .then((result) => {
                if (cancelled) return;
                const claims = result.organizations.map((o) => o.company_claim).filter(Boolean);
                if (user.company && !claims.includes(user.company)) claims.push(user.company);
                setCompanies(claims.sort((a, b) => a.localeCompare(b)));
            })
            // The list needs super_admin: without it the company stays as it is.
            .catch(() => { if (!cancelled) setCompanies([]); });
        return () => { cancelled = true; };
    }, [authToken, user.company]);

    const save = async () => {
        const changes: { name?: string; company?: string } = {};
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Name is required.');
            return;
        }
        if (trimmed !== (user.name || '')) changes.name = trimmed;
        if (company && company !== (user.company || '')) changes.company = company;
        if (Object.keys(changes).length === 0) {
            onCancel();
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await api.patchUser(authToken, user.id, changes);
            onSaved(changes);
        } catch (e: any) {
            setError(e?.message || 'Update failed');
            setSubmitting(false);
        }
    };

    const companyChanged = company !== '' && company !== (user.company || '');

    return (
        <ConfirmDialog
            open
            title={`Edit ${user.email}`}
            description={(
                <span className="grid gap-3">
                    <label className="flex flex-col gap-1 text-fg-secondary">
                        Name
                        <input
                            value={name}
                            maxLength={256}
                            onChange={(e) => setName(e.target.value)}
                            className={FIELD_CLASS}
                        />
                    </label>
                    <label className="flex flex-col gap-1 text-fg-secondary">
                        Company
                        {companies === null ? (
                            <span className="text-fg-tertiary">Loading organizations…</span>
                        ) : companies.length === 0 ? (
                            <span className="text-fg">
                                {user.company || '—'}
                                <span className="block text-fg-tertiary">Only a super admin can change the company.</span>
                            </span>
                        ) : (
                            <select
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                className={FIELD_CLASS}
                            >
                                {!user.company && <option value="">—</option>}
                                {companies.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        )}
                    </label>
                    {companyChanged && (
                        <span className="text-fg-secondary">
                            Moving this person to {company} gives them that organization's plan, modules
                            and workspace, and takes them out of {user.company || 'their current one'}.
                        </span>
                    )}
                </span>
            )}
            error={error || undefined}
            confirmLabel="Save"
            submitting={submitting}
            onConfirm={save}
            onCancel={() => { if (!submitting) onCancel(); }}
        />
    );
}

function UsersTable({ authToken, currentUserUid, refreshKey }: UsersTableProps) {
    const { showToast } = useToast();
    const deleteDialogTitleId = React.useId();
    const impersonateDialogTitleId = React.useId();
    const deleteConfirmInputRef = useRef<HTMLInputElement>(null);
    // BAR-135: impersonation is a super_admin-only capability; the row action is
    // shown only to super_admins (the server also enforces the gate).
    const { userData } = useAuth();
    const { startImpersonation } = useAuthActions();
    const canImpersonate = (userData as any)?.role === 'super_admin';
    const [users, setUsers] = useState<UserRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
    const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [impersonateTarget, setImpersonateTarget] = useState<UserRow | null>(null);
    const [impersonateReason, setImpersonateReason] = useState('');
    const [impersonateError, setImpersonateError] = useState<string | null>(null);
    const [impersonating, setImpersonating] = useState(false);
    const [pendingRoleChange, setPendingRoleChange] = useState<PendingRoleChange | null>(null);
    const [roleChangeSubmitting, setRoleChangeSubmitting] = useState(false);
    const [roleChangeError, setRoleChangeError] = useState<string | null>(null);
    const [editTarget, setEditTarget] = useState<UserRow | null>(null);

    const loadUsers = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await api.listUsers(authToken);
            setUsers(result);
        } catch (e: any) {
            setError(e?.message || 'Failed to load users');
        } finally {
            setIsLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers, refreshKey]);

    const setRowBusy = (uid: string, busy: boolean) => {
        setRowStates((s) => ({ ...s, [uid]: busy ? 'busy' : 'idle' }));
    };

    const handleToggleDisabled = async (user: UserRow) => {
        setRowBusy(user.id, true);
        try {
            await api.patchUser(authToken, user.id, { disabled: user.is_active });
            // Optimistic update.
            setUsers((u) =>
                u.map((x) => (x.id === user.id ? { ...x, is_active: !x.is_active } : x))
            );
            showToast({
                variant: 'success',
                message: `${user.email} ${user.is_active ? 'disabled' : 'enabled'}`,
            });
        } catch (e: any) {
            showToast({ variant: 'error', message: e?.message || 'Update failed' });
        } finally {
            setRowBusy(user.id, false);
        }
    };

    const handleChangeRole = (user: UserRow, newRole: string) => {
        if (newRole === user.role) return;
        setRoleChangeError(null);
        setPendingRoleChange({ user, newRole });
    };

    const confirmRoleChange = async () => {
        if (!pendingRoleChange || roleChangeSubmitting) return;
        const { user, newRole } = pendingRoleChange;
        setRoleChangeSubmitting(true);
        setRoleChangeError(null);
        setRowBusy(user.id, true);
        try {
            await api.patchUser(authToken, user.id, { role: newRole });
            setUsers((u) => u.map((x) => (x.id === user.id ? { ...x, role: newRole } : x)));
            showToast({ variant: 'success', message: `${user.email} role: ${newRole}` });
            setPendingRoleChange(null);
        } catch (e: any) {
            setRoleChangeError(e?.message || 'Role change failed');
        } finally {
            setRowBusy(user.id, false);
            setRoleChangeSubmitting(false);
        }
    };

    const handleResendInvite = async (user: UserRow) => {
        setRowBusy(user.id, true);
        try {
            const result = await api.resendInvite(authToken, user.id);
            const expires = formatDateTime(result.invite_expires_at, { context: 'date' });
            if (result.email_sent) {
                showToast({
                    variant: 'success',
                    message: `Invite resent to ${user.email}. Link valid until ${expires}.`,
                });
            } else {
                // Email failed — copy the backup link to clipboard so the
                // admin can share manually. Keep the toast around longer
                // so they actually see it.
                if (result.invite_link) {
                    try { await navigator.clipboard.writeText(result.invite_link); } catch {}
                }
                showToast({
                    variant: 'error',
                    message: `Email delivery failed for ${user.email}. Backup link copied to clipboard (expires ${expires}).`,
                });
            }
        } catch (e: any) {
            showToast({ variant: 'error', message: e?.message || 'Resend failed' });
        } finally {
            setRowBusy(user.id, false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        if (deleteConfirmText !== confirmDelete.email) {
            setDeleteError('Email confirmation does not match.');
            return;
        }
        // Capture before any async/state changes so the finally clause is
        // independent of `confirmDelete` clearing during the success path.
        const targetUid = confirmDelete.id;
        const targetEmail = confirmDelete.email;
        setDeleteError(null);
        setRowBusy(targetUid, true);
        try {
            await api.deleteUser(authToken, targetUid);
            setUsers((u) => u.filter((x) => x.id !== targetUid));
            showToast({ variant: 'success', message: `${targetEmail} deleted` });
            setConfirmDelete(null);
            setDeleteConfirmText('');
        } catch (e: any) {
            setDeleteError(e?.message || 'Delete failed');
        } finally {
            setRowBusy(targetUid, false);
        }
    };

    const handleConfirmImpersonate = async () => {
        if (!impersonateTarget) return;
        setImpersonating(true);
        setImpersonateError(null);
        try {
            await startImpersonation?.(impersonateTarget.id, impersonateReason.trim() || undefined);
            // On success the app swaps identity, tears down this session, and
            // navigates home — this panel unmounts, so no cleanup here.
        } catch (e: any) {
            setImpersonateError(e?.message || 'Failed to start impersonation');
            setImpersonating(false);
        }
    };

    const closeImpersonateDialog = () => {
        if (impersonating) return;
        setImpersonateTarget(null);
        setImpersonateReason('');
        setImpersonateError(null);
    };

    if (isLoading) {
        return <p className="text-sm text-fg-secondary">Loading users…</p>;
    }
    if (error) {
        return (
            <div className="text-sm text-danger">
                <p>{error}</p>
                <button onClick={loadUsers} className="mt-2 underline">Retry</button>
            </div>
        );
    }

    return (
        <>
            <div className="overflow-x-auto rounded-md border border-border-default">
                <table className="min-w-full divide-y divide-border-default">
                    <thead className="bg-bg-secondary">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Email</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Company</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Role</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-fg-secondary uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-fg-secondary uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-border-default">
                        {users.map((user) => {
                            const isSelf = user.id === currentUserUid;
                            const busy = rowStates[user.id] === 'busy';
                            return (
                                <tr key={user.id} className={isSelf ? 'bg-bg-secondary/50' : ''}>
                                    <td className="px-4 py-3 text-sm text-fg whitespace-nowrap">
                                        {user.email}
                                        {isSelf && <span className="ml-2 text-xs text-fg-secondary">(you)</span>}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-fg">{user.name}</td>
                                    <td className="px-4 py-3 text-sm text-fg-secondary">{user.company || '—'}</td>
                                    <td className="px-4 py-3 text-sm">
                                        {/* A super_admin row renders as TEXT, not a select: this
                                            panel offers only user/admin (super_admin is granted
                                            solely by the guarded writer — bootstrap script or a
                                            super_admin caller), so a <select value="super_admin">
                                            has no matching option and the browser falls back to
                                            displaying "user" — misreporting god-mode as a plain
                                            user, and one stray change event would demote them. */}
                                        {isSelf || user.role === 'super_admin' ? (
                                            <span className="text-fg">{user.role}</span>
                                        ) : (
                                            <select
                                                disabled={busy}
                                                value={user.role}
                                                onChange={(e) => handleChangeRole(user, e.target.value)}
                                                className="rounded border border-border-default px-2 py-1 text-sm bg-surface"
                                            >
                                                <option value="user">user</option>
                                                <option value="admin">admin</option>
                                            </select>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                        {!user.has_password && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-bg-secondary text-fg-secondary border border-border-default mr-1">
                                                Invited
                                            </span>
                                        )}
                                        {user.mcp_agent_access && (
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-bg-secondary text-fg-secondary border border-border-default mr-1"
                                                title="May use the BARK agent over MCP (Claude / Codex) — set by BARK policy, not editable here"
                                            >
                                                MCP agent
                                            </span>
                                        )}
                                        <span
                                            className={
                                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs ' +
                                                (user.is_active
                                                    ? 'bg-success/10 text-success'
                                                    : 'bg-danger/10 text-danger')
                                            }
                                        >
                                            {user.is_active ? 'Active' : 'Disabled'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                                        {isSelf ? (
                                            <span className="text-fg-tertiary text-xs">—</span>
                                        ) : (
                                            <div className="inline-flex gap-2">
                                                <button
                                                    disabled={busy}
                                                    onClick={() => setEditTarget(user)}
                                                    className="text-xs underline hover:no-underline text-fg disabled:opacity-50"
                                                >
                                                    Edit
                                                </button>
                                                {!user.has_password && (
                                                    <button
                                                        disabled={busy}
                                                        onClick={() => handleResendInvite(user)}
                                                        className="text-xs underline hover:no-underline text-accent disabled:opacity-50"
                                                    >
                                                        Resend invite
                                                    </button>
                                                )}
                                                <button
                                                    disabled={busy}
                                                    onClick={() => handleToggleDisabled(user)}
                                                    className="text-xs underline hover:no-underline text-fg disabled:opacity-50"
                                                >
                                                    {user.is_active ? 'Disable' : 'Enable'}
                                                </button>
                                                {canImpersonate && (
                                                    <button
                                                        disabled={busy}
                                                        onClick={() => {
                                                            setImpersonateTarget(user);
                                                            setImpersonateReason('');
                                                            setImpersonateError(null);
                                                        }}
                                                        className="text-xs underline hover:no-underline text-accent disabled:opacity-50"
                                                    >
                                                        Impersonate
                                                    </button>
                                                )}
                                                <button
                                                    disabled={busy}
                                                    onClick={() => {
                                                        setConfirmDelete(user);
                                                        setDeleteConfirmText('');
                                                        setDeleteError(null);
                                                    }}
                                                    className="text-xs underline hover:no-underline text-danger disabled:opacity-50"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <ConfirmDialog
                open={pendingRoleChange !== null}
                onCancel={() => {
                    if (!roleChangeSubmitting) {
                        setPendingRoleChange(null);
                        setRoleChangeError(null);
                    }
                }}
                onConfirm={confirmRoleChange}
                title={
                    pendingRoleChange
                        ? `Change role for ${pendingRoleChange.user.email}?`
                        : ''
                }
                description={
                    pendingRoleChange
                        ? `Change the role from "${pendingRoleChange.user.role}" to "${pendingRoleChange.newRole}". They must log out and back in for the change to take effect.`
                        : undefined
                }
                error={roleChangeError}
                confirmLabel="Change role"
                submitting={roleChangeSubmitting}
            />

            {editTarget && (
                <EditUserDialog
                    authToken={authToken}
                    user={editTarget}
                    onCancel={() => setEditTarget(null)}
                    onSaved={(changes) => {
                        setUsers((u) => u.map((x) => (x.id === editTarget.id ? { ...x, ...changes } : x)));
                        showToast({ variant: 'success', message: `${editTarget.email} updated` });
                        setEditTarget(null);
                    }}
                />
            )}

            {confirmDelete && (
                <DialogShell
                    open
                    onClose={() => {
                        setConfirmDelete(null);
                        setDeleteConfirmText('');
                        setDeleteError(null);
                    }}
                    labelledBy={deleteDialogTitleId}
                    backdropPolicy={deleteConfirmText !== '' ? 'static' : 'dismiss'}
                    initialFocusRef={deleteConfirmInputRef}
                    overlayClassName="flex items-center justify-center p-4"
                    panelClassName="w-full max-w-md p-6"
                >
                        <h3 id={deleteDialogTitleId} className="text-lg font-semibold text-fg">Delete user?</h3>
                        <p className="text-sm text-fg-secondary mt-2">
                            This will permanently remove <strong>{confirmDelete.email}</strong> from Firebase Auth.
                            Their reports and projects remain in the system.
                        </p>
                        <p className="text-sm text-fg-secondary mt-2">
                            Type the email address to confirm.
                        </p>
                        <input
                            ref={deleteConfirmInputRef}
                            value={deleteConfirmText}
                            onChange={(e) => {
                                setDeleteConfirmText(e.target.value);
                                setDeleteError(null);
                            }}
                            placeholder={confirmDelete.email}
                            className="mt-3 w-full rounded-md border border-border-default px-3 py-2 text-sm"
                        />
                        {deleteError && (
                            <p role="alert" className="mt-3 text-sm text-danger">
                                {deleteError}
                            </p>
                        )}
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                onClick={() => {
                                    setConfirmDelete(null);
                                    setDeleteConfirmText('');
                                    setDeleteError(null);
                                }}
                                className="px-4 py-2 text-sm rounded-md border border-border-default text-fg hover:bg-bg-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={deleteConfirmText !== confirmDelete.email}
                                onClick={handleDelete}
                                className="px-4 py-2 text-sm rounded-md bg-danger text-white disabled:opacity-50"
                            >
                                Delete
                            </button>
                        </div>
                </DialogShell>
            )}

            {impersonateTarget && (
                <DialogShell
                    open
                    onClose={closeImpersonateDialog}
                    labelledBy={impersonateDialogTitleId}
                    backdropPolicy={impersonating ? 'static' : 'dismiss'}
                    overlayClassName="flex items-center justify-center p-4"
                    panelClassName="w-full max-w-md p-6"
                >
                    <h3 id={impersonateDialogTitleId} className="text-lg font-semibold text-fg">
                        Impersonate user?
                    </h3>
                    <p className="text-sm text-fg-secondary mt-2">
                        You will act as <strong>{impersonateTarget.email}</strong> for up to 60 minutes.
                        Every action is audited under your identity. A persistent banner shows while
                        impersonating — use <strong>Exit</strong> to return to your account.
                    </p>
                    <label className="block text-sm text-fg mt-4 mb-1" htmlFor="impersonate-reason">
                        Reason <span className="text-fg-tertiary">(optional)</span>
                    </label>
                    <textarea
                        id="impersonate-reason"
                        value={impersonateReason}
                        onChange={(e) => setImpersonateReason(e.target.value)}
                        rows={2}
                        placeholder="e.g. reproducing a reported issue"
                        disabled={impersonating}
                        className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-surface disabled:opacity-60"
                    />
                    {impersonateError && (
                        <p role="alert" className="mt-3 text-sm text-danger">
                            {impersonateError}
                        </p>
                    )}
                    <div className="mt-6 flex justify-end gap-2">
                        <button
                            onClick={closeImpersonateDialog}
                            disabled={impersonating}
                            className="px-4 py-2 text-sm rounded-md border border-border-default text-fg hover:bg-bg-secondary disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmImpersonate}
                            disabled={impersonating}
                            className="px-4 py-2 text-sm rounded-md bg-[color:var(--primary)] text-on-primary hover:bg-[color:var(--primary-hover)] disabled:opacity-60"
                        >
                            {impersonating ? 'Starting…' : 'Impersonate'}
                        </button>
                    </div>
                </DialogShell>
            )}
        </>
    );
}

export default UsersTable;
